/* eslint-disable prettier/prettier */
/** @jsxImportSource @emotion/react */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Grid,
  ListItem,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  IconButton,
  Modal,
  List,
} from "@mui/material";
import { css, keyframes } from "@emotion/react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Logo from "../../assets/svg/Logo";
import bdsLogin from "../../assets/images/bdsLogin.jpg";
import InputField from "../../components/common/new/InputField/InputField";
import ShowPassword from "../../assets/svg/ShowPassword";
import HidePassword from "../../assets/svg/HidePasssword";
import Button from "../../components/common/Button";
import { createArrayByLen } from "../../utils/helper";
import { AlertStatus, OTP_LEN } from "../../contants";
import OTPInput from "../../components/common/new/OTPInput/OTPInput";
import { ToastAlertContext } from "../../config/providers/ToastAlert";
import Forgot from "./forgotPassword";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const wrapper = css`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  background-image: url(${bdsLogin});
  background-size: cover;
  background-position: center;
`;

const centerContainer = css`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 15px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const contentBox = css`
  display: flex;
  flex-direction: row;
  max-width: 920px;
  width: 100%;
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.2);
  animation: ${fadeIn} 1s ease-out;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const tipsBox = css`
  flex: 1;
  min-width: 300px;
  background: rgb(18 61 124);
  border-radius: 0px 12px 12px 0px;
  color: white;
  padding: 30px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 768px) {
    border-radius: 0px 0px 12px 12px;
  }
`;

const formBox = css`
  flex: 1;
  min-width: 300px;
  padding: 30px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const otpContainer = css`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin: 24px 0;
`;

const footer = css`
  width: 100%;
  text-align: center;
  padding: 12px 0;
  color: #ffffff;
`;

function PasswordStrengthBar({ password }) {
  const calculateStrength = () => {
    let strength = 0;
    if (password.length >= 8 && /\d/.test(password)) strength += 1;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 1;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength += 1;
    return strength;
  };

  const strength = calculateStrength();

  const levels = [
    { label: "Poor", color: "#E87466" },
    { label: "Good", color: "#FFDB9B" },
    { label: "Excellent", color: "#9AE68B" },
  ];

  return password ? (
    <Box sx={{ mb: 2 }}>
      <Grid container spacing={1}>
        {levels.map((level, i) => (
          <Grid
            item
            xs={4}
            key={`strength-${level.label}`}
            sx={{
              height: "5px",
              borderRadius: "15px",
              backgroundColor: level.color,
              opacity: strength > i ? 1 : 0.3,
            }}
          />
        ))}
      </Grid>
      <Typography
        variant="caption"
        sx={{
          mt: 1,
          color: strength > 0 ? levels[strength - 1].color : "#999",
          fontWeight: 500,
        }}
      >
        {strength > 0 ? levels[strength - 1].label : "Too Weak"}
      </Typography>
    </Box>
  ) : null;
}
PasswordStrengthBar.propTypes = {
  password: PropTypes.string.isRequired,
};

function ChangePassword() {
  const { memberId, authToken } = useParams();
  const toast = useContext(ToastAlertContext);
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [forgotModal, setForgotModal] = useState(false);

  const handleCopyPaste = (e) => e.preventDefault();

  const postData = async (e) => {
    e.preventDefault();
    const { elements } = e.target;
    const otpValue = createArrayByLen(OTP_LEN)
      .map((_, idx) => elements[`otp-${idx + 1}`]?.value || "")
      .join("");

    if (otpValue.length < OTP_LEN) {
      toast.show("Please enter OTP", AlertStatus.ERROR);
      return;
    }

    if (newPassword.length < 8) {
      toast.show(
        "New password must be at least 8 characters",
        AlertStatus.ERROR
      );
      return;
    }

    if (confirmPassword !== newPassword) {
      toast.show("Confirm password does not match", AlertStatus.ERROR);
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/updatePasswordByOtp`,
        {
          memberId,
          otp: Number(otpValue),
          newPassword,
        },
        {
          headers: {
            "X-Auth-Token": authToken,
          },
        }
      );

      if (response.data.code === 200) {
        toast.show(response.data.message, AlertStatus.SUCCESS);
        setTimeout(() => navigate("/"), 3000);
      } else {
        toast.show(response.data.message || "Error", AlertStatus.ERROR);
      }
    } catch (err) {
      toast.show(
        err?.response?.data?.message || err.message,
        AlertStatus.ERROR
      );
    }
  };

  return (
    <>
      <Box css={wrapper}>
        <Box css={centerContainer}>
          <Box css={contentBox}>
            <Box
              css={formBox}
              component="form"
              onSubmit={postData}
              autoComplete="off"
            >
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Logo style={{ maxWidth: 80 }} />
                <Typography variant="h5" sx={{ mt: 2 }}>
                  Change Password
                </Typography>
                <Typography variant="body2" sx={{ color: "#666", mt: 1 }}>
                  Enter the OTP sent to your email/mobile and set a new
                  password.
                </Typography>
              </Box>

              <Box css={otpContainer}>
                {createArrayByLen(OTP_LEN).map((_, idx) => {
                  const key = `otp-${idx + 1}`;
                  return (
                    <OTPInput
                      key={key}
                      id={idx + 1}
                      name={key}
                      style={{
                        height: 40,
                        background: "#f2f2f2",
                        fontSize: 16,
                      }}
                    />
                  );
                })}
              </Box>

              <InputField
                required
                label="New Password"
                name="newPassword"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onCopy={handleCopyPaste}
                onPaste={handleCopyPaste}
                InputProps={{
                  disableUnderline: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? <ShowPassword /> : <HidePassword />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              <PasswordStrengthBar password={newPassword} />

              <InputField
                required
                label="Confirm Password"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onCopy={handleCopyPaste}
                onPaste={handleCopyPaste}
                InputProps={{
                  disableUnderline: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                      >
                        {showConfirmPassword ? (
                          <ShowPassword />
                        ) : (
                          <HidePassword />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                variant="contained"
                style={{
                  width: "100%",
                  height: 48,
                  textTransform: "uppercase",
                }}
                label="Change Password"
              />
            </Box>

            <Box css={tipsBox}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Tips for creating a strong password.
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: "white" }}>
                Creating a strong and secure password is an important part of
                protecting your BSG Account.
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: "white" }}>
                Here are some tips for creating a strong password:
              </Typography>
              <List>
                {[
                  "Use at least 8 characters",
                  "Use a mix of letters (uppercase and lowercase), numbers and symbols",
                  "Don't use your name, username or account name",
                  'Avoid predictable passwords such as "BSG123#"',
                ].map((tip) => (
                  <ListItem key={tip} sx={{ color: "#fff" }}>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: "#fff" }} />
                    </ListItemIcon>
                    <ListItemText primary={tip} />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        </Box>

        <Box css={footer}>
          <Typography variant="caption">
            ©Copyright Bharat Soka Gakkai 2026
          </Typography>
        </Box>
      </Box>

      <Modal open={forgotModal} onClose={() => setForgotModal(false)}>
        <Box sx={{ margin: "5% auto", maxWidth: 700, outline: "none" }}>
          <Forgot onClose={() => setForgotModal(false)} />
        </Box>
      </Modal>
    </>
  );
}

export default ChangePassword;
